/**
* @param {Date} date
* @returns {Date}
*/
function startOfWeek(date) {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate() - date.getDay());
}

/**
* @param {Date} date
* @returns {Date}
*/
function endOfWeek(date) {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate() + (6 - date.getDay()));
}

/**
* @param {Date} date
* @returns {Date}
*/
function startOfDay(date) {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0, 0);
}

/**
* @param {Date} date
* @returns {Date}
*/
function endOfDay(date) {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate(), 23, 59, 59, 999);
}

/**
* @param {Date} date
* @returns {Date}
*/
function firstOfMonth(date) {
  return new Date(date.getFullYear(), date.getMonth());
}

/**
* @param {Date} date
* @returns {Date}
*/
function lastOfMonth(date) {
  return new Date(date.getFullYear(), date.getMonth() + 1, 0);
}

/**
* @param {Date} date
* @returns {Date}
*/
function previousMonth(date) {
  return new Date(date.getFullYear(), date.getMonth() - 1);
}

/**
* @param {Date} date
* @returns {Date}
*/
function nextMonth(date) {
  return new Date(date.getFullYear(), date.getMonth() + 1);
}

/**
* @param {Date} date
* @returns {Date}
*/
function previousWeek(date) {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate() - 7);
}

/**
* @param {Date} date
* @returns {Date}
*/
function nextWeek(date) {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate() + 7);
}

/**
* @param {Date} date
* @returns {Date}
*/
function previousDay(date) {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate() - 1);
}

/**
* @param {Date} date
* @returns {Date}
*/
function nextDay(date) {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate() + 1);
}

/**
* @param {Date} startDate
* @param {Date} endDate
* @returns {Date[]}
*/
function buildRange(startDate, endDate, dateIncrementer) {
  if (startDate > endDate) {
    throw new Error();
  }

  const dates = [startDate];

  if (startDate.getTime() === endDate.getTime()) {
    return dates;
  }

  let next = dateIncrementer(startDate);
  while (next.getTime() < endDate.getTime()) {
    dates.push(next);
    next = dateIncrementer(next);
  }
  dates.push(endDate);

  return dates;
}

/**
* @param {Date} startDate
* @param {Date} endDate
* @returns {Date[]}
*/
function getMonthRange(startDate, endDate) {
  const startMonth = firstOfMonth(startDate);
  const endMonth = firstOfMonth(endDate);

  return buildRange(startMonth, endMonth, nextMonth);
}

/**
* @param {Date} startDate
* @param {Date} endDate
* @returns {Date[]}
*/
function getWeekRange(startDate, endDate) {
  const startWeek = startOfWeek(startDate);
  const endWeek = startOfWeek(endDate);

  return buildRange(startWeek, endWeek, nextWeek);
}

/**
* @param {Number} number
* @param {Number} padTo
* @returns {string}
*/
function padNumber(number, padTo = 2) {
  return `${number}`.padStart(padTo, '0');
}


/**
* @param {Date} date
* @returns {string}
*/
function getISODateString(date) {
  return `${date.getFullYear()}-${padNumber(date.getMonth() + 1)}-${padNumber(date.getDate())}`;
}


/**
* @param {Date} date
* @param {Date|null} minDate
* @param {Date|null} maxDate
* @returns {Date}
*/
function constrainDate(date, minDate = null, maxDate = null) {
  let time = date.getTime();
  const min = minDate === null ? -Infinity : minDate.getTime();
  const max = maxDate === null ? Infinity : maxDate.getTime();

  if (time < min) time = min;
  if (time > max) time = max;

  return new Date(time);
}

/**
* @param {string} string
* @returns {Date|null}
*/
function parseYYYYMMDD(string) {
  const regex = /^(\d{4})-(\d{2})-(\d{2})/;
  const matches = regex.exec(string);

  if (matches) {
    const [fullMatch, year, month, day] = matches;
    const date = new Date(parseInt(year, 10), parseInt(month, 10) - 1, parseInt(day, 10));

    return date;
  }

  return null;
}

/**
* @param {Date} date
* @param {Date} startDate
* @param {Date} endDate
* @returns {boolean}
*/
function isBetweenDates(date, startDate, endDate) {
  const time = date.getTime();
  if (time < startDate.getTime()) return false;
  if (time > endDate.getTime()) return false;
  return true;
}

/**
* @param {Date} date1
* @param {Date} date2
* @returns {boolean}
*/
function areAdjacentMonths(date1, date2) {
  const month1 = (date1.getFullYear() * 12) + date1.getMonth();
  const month2 = (date2.getFullYear() * 12) + date2.getMonth();

  return Math.abs(month1 - month2) === 1;
}


/**
* @param {Date} date1
* @param {Date} date2
* @returns {boolean}
*/
function areSameYear(date1, date2) {
  return date1.getFullYear() === date2.getFullYear();
}

/**
* @param {Date} date1
* @param {Date} date2
* @returns {boolean}
*/
function areDifferentYears(date1, date2) {
  return !areSameYear(date1, date2);
}

/**
* @param {Date} date1
* @param {Date} date2
* @returns {boolean}
*/
function areSameMonth(date1, date2) {
  return areSameYear(date1, date2) && date1.getMonth() === date2.getMonth();
}

/**
* @param {Date} date1
* @param {Date} date2
* @returns {boolean}
*/
function areDifferentMonths(date1, date2) {
  return !areSameMonth(date1, date2);
}


/**
* @param {Date} date1
* @param {Date} date2
* @returns {boolean}
*/
function areSameDay(date1, date2) {
  return areSameMonth(date1, date2) && date1.getDate() === date2.getDate();
}

/**
* @param {Date} date1
* @param {Date} date2
* @returns {boolean}
*/
function areDifferentDays(date1, date2) {
  return !areSameDay(date1, date2);
}


export {
  firstOfMonth,
  lastOfMonth,
  previousDay,
  nextDay,
  previousWeek,
  nextWeek,
  previousMonth,
  nextMonth,
  getWeekRange,
  getMonthRange,
  getISODateString,
  padNumber,
  startOfWeek,
  endOfWeek,
  constrainDate,
  isBetweenDates,
  areSameYear,
  areSameMonth,
  areSameDay,
  areDifferentYears,
  areDifferentMonths,
  areDifferentDays,
  areAdjacentMonths,
  endOfDay,
  startOfDay,
  parseYYYYMMDD,
};
